import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2, X, Loader2, ListChecks, Crown, KeyRound, Ban, Unlock, MessagesSquare, Users, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MenuPermissionsModal from "../components/MenuPermissionsModal";
import { SquadraPicker, squadraPayload } from "../components/SquadraPicker";

const EMPTY_SQ = { squadra_id: "", nuova_squadra_nome: "" };
const EMPTY_FORM = { email: "", password: "", name: "", role: "operatore", ...EMPTY_SQ };
const LEADER_ROLES = ["responsabile", "supervisore"];
const ROLES = ["admin", "operatore", "tecnico", "magazzino", "visualizzatore", "supervisore", "responsabile", "installatore"];

export default function Utenti() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [credUser, setCredUser] = useState(null);
  const [credForm, setCredForm] = useState({ email: "", password: "" });
  const [form, setForm] = useState(EMPTY_FORM);
  const [squadre, setSquadre] = useState([]);
  const [sqUser, setSqUser] = useState(null);
  const [sqForm, setSqForm] = useState(EMPTY_SQ);

  const load = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/users"), api.get("/squadre")]);
      setList(u.data); setSquadre(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { squadra_id, nuova_squadra_nome, ...base } = form;
      const body = LEADER_ROLES.includes(form.role) ? { ...base, ...squadraPayload(form) } : base;
      await api.post("/auth/register", body);
      toast.success("Utente creato");
      setShowForm(false); setForm(EMPTY_FORM);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Eliminato"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openCred = (u) => { setCredUser(u); setCredForm({ email: u.email, password: "" }); };

  const saveCred = async (e) => {
    e.preventDefault();
    try {
      const body = {};
      if (credForm.email && credForm.email !== credUser.email) body.email = credForm.email;
      if (credForm.password) body.password = credForm.password;
      if (!Object.keys(body).length) { toast.info("Nessuna modifica da salvare"); return; }
      await api.put(`/users/${credUser.id}/credentials`, body);
      toast.success("Credenziali aggiornate");
      setCredUser(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleBlocked = async (u) => {
    const msg = u.blocked
      ? `Sbloccare l'accesso di ${u.email}?`
      : `Bloccare l'accesso di ${u.email}? I suoi dati e i conteggi bonus restano intatti.`;
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/users/${u.id}/blocked`, { blocked: !u.blocked });
      toast.success(u.blocked ? "Accesso sbloccato" : "Accesso bloccato");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openSquadra = (u) => { setSqUser(u); setSqForm({ squadra_id: u.squadra_responsabile?.id || "", nuova_squadra_nome: "" }); };

  const saveSquadra = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${sqUser.id}/squadra`, squadraPayload(sqForm));
      toast.success("Squadra aggiornata");
      setSqUser(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="utenti-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Utenti</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Gestione accessi e ruoli</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="new-user-btn">
          <Plus className="w-4 h-4" /> Nuovo utente
        </button>
      </div>

      {loading ? <div className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div> :
      <div className="card-shell overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--surface-muted)" }}>
            <tr>
              {["Nome", "Email", "Ruolo", "Squadra", "Menu visibili", "Creato il", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                <td className="px-4 py-3 font-semibold">
                  {u.name}
                  {u.is_super && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`super-badge-${u.id}`}>
                      <Crown className="w-3 h-3" /> SUPER ADMIN
                    </span>
                  )}
                  {u.blocked && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FEE2E2", color: "#B30000" }} data-testid={`blocked-badge-${u.id}`}>
                      <Ban className="w-3 h-3" /> BLOCCATO
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3"><span className="badge badge-in_corso capitalize">{u.role}</span></td>
                <td className="px-4 py-3" data-testid={`user-squadra-${u.id}`}>
                  {LEADER_ROLES.includes(u.role) ? (
                    <button onClick={() => openSquadra(u)} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md hover:bg-black/5 transition-colors"
                      title="Modifica squadra" data-testid={`edit-squadra-${u.id}`}>
                      {u.squadra_responsabile ? (
                        <><span className="w-2.5 h-2.5 rounded-full" style={{ background: u.squadra_responsabile.color || "#1B5E20" }} />{u.squadra_responsabile.name}</>
                      ) : <span style={{ color: "#B30000" }}>Nessuna squadra</span>}
                      <Pencil className="w-3 h-3 opacity-60" />
                    </button>
                  ) : <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span>}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }} data-testid={`menu-perms-${u.id}`}>
                  {u.is_super ? "Tutte (super admin)" : u.menu_permissions == null ? "Tutte" : `${u.menu_permissions.length} voci`}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{new Date(u.created_at).toLocaleDateString("it-IT")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {me?.is_super && !u.is_super && (
                    <button onClick={() => setPermUser(u)} className="p-1.5 rounded hover:bg-black/5" title="Permessi menu" data-testid={`edit-permissions-${u.id}`}>
                      <ListChecks className="w-4 h-4" style={{ color: "var(--primary)" }} />
                    </button>
                  )}
                  {me?.is_super && (!u.is_super || u.id === me?.id) && (
                    <button onClick={() => openCred(u)} className="p-1.5 rounded hover:bg-black/5" title="Modifica email/password di accesso" data-testid={`edit-credentials-${u.id}`}>
                      <KeyRound className="w-4 h-4" style={{ color: "#92400E" }} />
                    </button>
                  )}
                  {me?.is_super && !u.is_super && u.id !== me?.id && (
                    <button onClick={() => toggleBlocked(u)} className="p-1.5 rounded hover:bg-black/5"
                      title={u.blocked ? "Sblocca accesso" : "Blocca accesso (dati e bonus conservati)"} data-testid={`toggle-blocked-${u.id}`}>
                      {u.blocked ? <Unlock className="w-4 h-4" style={{ color: "#1B5E20" }} /> : <Ban className="w-4 h-4" style={{ color: "#B30000" }} />}
                    </button>
                  )}
                  {me?.is_super && (() => {
                    const mp = u.menu_permissions;
                    const inChat = !u.blocked && (u.role === "admin" ? (!mp || mp.includes("chat")) : (["responsabile", "supervisore"].includes(u.role) || (mp || []).includes("chat")));
                    return (
                      <span className="p-1.5" title={inChat ? "Membro delle chat di installazione" : "Non è nelle chat (attiva la voce Comunicazioni nei permessi menu)"} data-testid={`chat-member-indicator-${u.id}`}>
                        <MessagesSquare className="w-4 h-4" style={{ color: inChat ? "#1B5E20" : "#9CA3AF" }} />
                      </span>
                    );
                  })()}
                  {u.id !== me?.id && !u.is_super && (
                    <button onClick={() => remove(u)} className="p-1.5 rounded hover:bg-black/5"><Trash2 className="w-4 h-4" style={{ color: "#B30000" }} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      {permUser && (
        <MenuPermissionsModal utente={permUser} onClose={() => setPermUser(null)} onSaved={load} />
      )}

      {credUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setCredUser(null)}>
          <div className="card-shell w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Credenziali · {credUser.name}</h2>
              <button onClick={() => setCredUser(null)} className="p-1.5 rounded hover:bg-black/5" data-testid="cred-close"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={saveCred} className="p-6 space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Email di accesso</label>
                <input required type="email" data-testid="cred-email" value={credForm.email} onChange={(e) => setCredForm({ ...credForm, email: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nuova password (lascia vuoto per non cambiarla)</label>
                <input type="password" data-testid="cred-password" placeholder="Min 6 caratteri" value={credForm.password} onChange={(e) => setCredForm({ ...credForm, password: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCredUser(null)} className="btn-secondary">Annulla</button>
                <button type="submit" className="btn-primary" data-testid="cred-submit">Salva credenziali</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {sqUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setSqUser(null)}>
          <div className="card-shell w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="squadra-modal">
            <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}><Users className="w-5 h-5" /> Squadra · {sqUser.name}</h2>
              <button onClick={() => setSqUser(null)} className="p-1.5 rounded hover:bg-black/5" data-testid="squadra-modal-close"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={saveSquadra} className="p-6 space-y-4">
              <SquadraPicker squadre={squadre} value={sqForm} onChange={setSqForm} testPrefix="edit-squadra" />
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>L'utente verrà aggiunto ai membri della squadra come responsabile e rimosso dalla squadra precedente.</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setSqUser(null)} className="btn-secondary">Annulla</button>
                <button type="submit" className="btn-primary" data-testid="squadra-submit">Salva squadra</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowForm(false)}>
          <div className="card-shell w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Nuovo utente</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <input required data-testid="u-name" placeholder="Nome completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              <input required type="email" data-testid="u-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              <input required type="password" data-testid="u-pw" placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              <select data-testid="u-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}>
                {ROLES.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
              </select>
              {LEADER_ROLES.includes(form.role) && (
                <SquadraPicker squadre={squadre} value={form} onChange={(v) => setForm({ ...form, ...v })} />
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annulla</button>
                <button type="submit" className="btn-primary" data-testid="u-submit">Crea utente</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
